import { c as _c } from "react/compiler-runtime";
/* eslint-disable import/no-unresolved */
import React, { useRef, useEffect } from 'react';
import classNames from 'classnames';
import loadable from '@loadable/component';
import { useMediaQuery } from 'react-responsive';
import PropTypes from 'prop-types';
import IconArrowLeftWhite from 'components/icons/Slider/iconArrowLeftWhite';
import IconArrowRightWhite from 'components/icons/Slider/iconArrowRightWhite';
import 'pure-react-carousel/dist/react-carousel.es.css';
import useScreen from 'hooks/useScreen';
import useIsBrowser from 'hooks/useIsBrowser';
import isNewConsumerApp from 'commons/isNewConsumerApp';
import CustomCarouselArrows from './CustomCarouselArrows';
const Carousel = loadable(() => import('./WrappedCarousel'));
const ForwardRefCarousel = React.forwardRef((props, ref) => {
  const $ = _c(3);
  let t0;
  if ($[0] !== props || $[1] !== ref) {
    t0 = <Carousel carouselRef={ref} {...props} />;
    $[0] = props;
    $[1] = ref;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
});
let firstClientX;
let clientX;
const preventTouch = e => {
  const minValue = 5; // threshold

  clientX = e.touches[0].clientX - firstClientX;

  // Vertical scrolling does not work when you start swiping horizontally.
  if (Math.abs(clientX) > minValue) {
    e.preventDefault();
    e.returnValue = false;
    return false;
  }
  return null;
};
const touchStart = e => {
  firstClientX = e.touches[0].clientX;
};
const SliderComponent = props => {
  const $ = _c(53);
  const containerRef = useRef(null);
  const carouselRef = useRef(null);
  const isBrowser = useIsBrowser();
  const {
    isMobile,
    isDesktop
  } = useScreen();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      query: "(min-width: 1281px)"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const isLargeDesktopDevice = useMediaQuery(t0);
  const isLargeDesktop = isBrowser ? isLargeDesktopDevice : false;
  const isMobileTabletOrSmallDesktop = !isLargeDesktop;
  const {
    children,
    deviceOverride: t1,
    disableSliding,
    fabricGridControls: t2,
    finalVisibleSlides,
    isMobileMaterialSwatches: t3,
    navType,
    variant,
    fullHeight: t4
  } = props;
  const deviceOverride = t1 === undefined ? false : t1;
  const fabricGridControls = t2 === undefined ? false : t2;
  const isMobileMaterialSwatches = t3 === undefined ? false : t3;
  const fullHeight = t4 === undefined ? false : t4;
  const [slideIndex, setSlideIndex] = React.useState(0);
  const showLeftAndRightPartially = finalVisibleSlides < 2 && finalVisibleSlides > 1;
  let t5;
  let t6;
  if ($[1] !== isMobile) {
    t5 = () => {
      const currentRef = containerRef.current;
      if (containerRef.current && !isMobile) {
        currentRef.addEventListener("touchstart", touchStart, {
          passive: true
        });
        currentRef.addEventListener("touchmove", preventTouch, {
          passive: true
        });
      }
      return () => {
        if (currentRef) {
          currentRef.removeEventListener("touchstart", touchStart, {
            passive: true
          });
          currentRef.removeEventListener("touchmove", preventTouch, {
            passive: true
          });
        }
      };
    };
    t6 = [containerRef, isMobile];
    $[1] = isMobile;
    $[2] = t5;
    $[3] = t6;
  } else {
    t5 = $[2];
    t6 = $[3];
  }
  useEffect(t5, t6);
  let t7;
  if ($[4] !== isMobile) {
    t7 = () => {
      if (isMobile || !carouselRef.current) {
        return;
      }
      if (!isMobile && carouselRef.current !== null) {
        setTimeout(() => {
          carouselRef.current?.setDimensions?.();
        }, 0);
      }
    };
    $[4] = isMobile;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] !== isMobile || $[7] !== showLeftAndRightPartially) {
    t8 = [carouselRef, isMobile, showLeftAndRightPartially];
    $[6] = isMobile;
    $[7] = showLeftAndRightPartially;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  useEffect(t7, t8);
  const finalSlides = children;
  let visibleSlides;
  if ($[9] !== deviceOverride || $[10] !== finalSlides.length || $[11] !== finalVisibleSlides || $[12] !== isLargeDesktop || $[13] !== isMobile || $[14] !== isMobileTabletOrSmallDesktop) {
    visibleSlides = 0;
    if (isLargeDesktop) {
      visibleSlides = finalVisibleSlides && finalVisibleSlides !== 4 ? finalVisibleSlides : finalSlides.length < 4 ? finalSlides.length : 4;
    }
    if (isMobileTabletOrSmallDesktop) {
      visibleSlides = finalSlides.length < 3 ? finalSlides.length : 3;
    }
    if (isMobile) {
      visibleSlides = finalSlides.length < 2 ? finalSlides.length : 2;
    }
    if (deviceOverride) {
      visibleSlides = finalVisibleSlides;
    }
    $[9] = deviceOverride;
    $[10] = finalSlides.length;
    $[11] = finalVisibleSlides;
    $[12] = isLargeDesktop;
    $[13] = isMobile;
    $[14] = isMobileTabletOrSmallDesktop;
    $[15] = visibleSlides;
  } else {
    visibleSlides = $[15];
  }
  if (!finalSlides.length) {
    const t9 = variant === "material-swatches-sidebar";
    const t10 = variant === "blog-slider";
    let t11;
    if ($[16] !== t10 || $[17] !== t9) {
      t11 = classNames("mt-5 [&_.slider]:min-h-[1px] [&_.slider]:m-auto [&_.slider-slide>img]:object-cover components [&_.slider-control-bottomcenter]:w-full", {
        "!mt-0": t9,
        "!min-h-[250px] max-[400px]:!min-h-[400px]": t10
      });
      $[16] = t10;
      $[17] = t9;
      $[18] = t11;
    } else {
      t11 = $[18];
    }
    let t12;
    if ($[19] !== t11) {
      t12 = <div className={t11} ref={containerRef} />;
      $[19] = t11;
      $[20] = t12;
    } else {
      t12 = $[20];
    }
    return t12;
  }
  const hideNav = visibleSlides >= finalSlides.length || navType === "";
  const t9 = !isMobileMaterialSwatches;
  const t10 = variant === "material-swatches-sidebar";
  const t11 = variant === "blog-slider";
  let t12;
  if ($[21] !== fabricGridControls || $[22] !== t10 || $[23] !== t11 || $[24] !== t9) {
    t12 = classNames("SliderComponent components [&_.slider-control-bottomcenter]:w-full", {
      "[&_.slider-control-centerleft]:items-stretch [&_.slider-control-centerleft]:!bottom-0 [&_.slider-control-centerleft]:flex [&_.slider-control-centerleft]:justify-stretch [&_.slider-control-centerleft]:absolute [&_.slider-control-centerleft]:!top-0 [&_.slider-control-centerleft]:![transform:none] [&_.slider-control-centerleft]:!w-[calc(9%_-_9px)] [&_.slider-control-centerright]:items-stretch [&_.slider-control-centerright]:!bottom-0 [&_.slider-control-centerright]:flex [&_.slider-control-centerright]:justify-stretch [&_.slider-control-centerright]:absolute [&_.slider-control-centerright]:!top-0 [&_.slider-control-centerright]:![transform:none] [&_.slider-control-centerright]:!w-[calc(9%_-_9px)]": fabricGridControls,
      "mt-5 [&_.slider]:min-h-[1px] [&_.slider]:m-auto [&_.slider-slide>img]:object-cover [&_.slider-control-centerleft]:!left-0 [&_.slider-control-centerright]:!right-0": t9,
      "!mt-0": t10,
      "pt-1 !mt-0": t11
    });
    $[21] = fabricGridControls;
    $[22] = t10;
    $[23] = t11;
    $[24] = t9;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== finalSlides.length || $[27] !== hideNav || $[28] !== isMobile || $[29] !== slideIndex || $[30] !== variant || $[31] !== visibleSlides) {
    t13 = variant === "blog-slider" && !isMobile && !hideNav && <div className="w-full flex justify-end gap-2 mb-2"><button type="button" className="group/SliderButton w-[50px] h-[50px] flex justify-center items-center opacity-100 text-[0] leading-[0] cursor-pointer text-transparent border border-solid border-gray-light6 outline-0 transition-all hover:border-brand bg-transparent [&_svg_path]:fill-gray" aria-label="Previous slide" disabled={slideIndex === 0} onClick={() => setSlideIndex(_temp)}><IconArrowLeftWhite /></button><button type="button" className="group/SliderButton w-[50px] h-[50px] flex justify-center items-center opacity-100 text-[0] leading-[0] cursor-pointer text-transparent border border-solid border-gray-light6 outline-0 transition-all hover:border-brand bg-transparent [&_svg_path]:fill-gray" aria-label="Next slide" disabled={slideIndex >= finalSlides.length - visibleSlides} onClick={() => setSlideIndex(i_0 => Math.min(i_0 + 1, finalSlides.length - visibleSlides))}><IconArrowRightWhite /></button></div>;
    $[26] = finalSlides.length;
    $[27] = hideNav;
    $[28] = isMobile;
    $[29] = slideIndex;
    $[30] = variant;
    $[31] = visibleSlides;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== disableSliding || $[34] !== fabricGridControls || $[35] !== finalSlides || $[36] !== finalVisibleSlides || $[37] !== fullHeight || $[38] !== hideNav || $[39] !== isDesktop || $[40] !== isMobile || $[41] !== isMobileMaterialSwatches || $[42] !== navType || $[43] !== props || $[44] !== showLeftAndRightPartially || $[45] !== slideIndex || $[46] !== variant || $[47] !== visibleSlides) {
    t14 = isMobile && !isMobileMaterialSwatches ? <div className="overflow-hidden"><div className={classNames("-ml-5 -mr-5 pl-5 pr-5 flex overflow-scroll overflow-y-hidden", "max-[767px]:ml-0 max-[767px]:mr-0 max-[767px]:pl-5 max-[767px]:pr-5", "[&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]", "h-[425px]", {
        "overflow-scroll overflow-y-hidden flex": variant === "blog-slider" || variant === "collections-slider"
      })}>{finalSlides}</div></div> : <>{!isNewConsumerApp && <ForwardRefCarousel ref={carouselRef} speed={showLeftAndRightPartially ? 400 : 200} height={fullHeight ? "100%" : isDesktop ? "450px" : "100%"} heightMode="max" slidesToShow={visibleSlides} cellSpacing={showLeftAndRightPartially ? 5 : 20} cellAlign={showLeftAndRightPartially ? "center" : undefined} wrapAround={showLeftAndRightPartially ? true : finalVisibleSlides < finalSlides.length} dragging={!showLeftAndRightPartially} swiping={!disableSliding} prevSlidesToScroll={showLeftAndRightPartially ? 1 : undefined} slideIndex={slideIndex} width="100%" renderCenterLeftControls={variant === "blog-slider" ? _temp2 : t15 => {
        const {
          previousSlide
        } = t15;
        if (hideNav || navType === "Dots" || finalSlides.length < visibleSlides) {
          return null;
        }
        return <button type="button" className={fabricGridControls ? "items-center [background:none] border-0 flex justify-center w-full hover:cursor-pointer focus:outline-0 focus-visible:outline-4 focus-visible:outline-offset-[-4px]" : classNames("group/SliderButton relative right-[20px] w-[50px] h-[50px] rounded-[100px] !flex justify-center items-center opacity-100 text-[0] leading-[0] cursor-pointer text-transparent border border-solid border-gray-light6 outline-0 [transition:all_.2s_ease-in] hover:border-brand !bg-white transform-[translateX(-50%)]", "[&_svg_path]:[transition:all_.2s_ease-in] [&_svg_path]:fill-gray", "group-hover/SliderButton:[&_svg_path]:fill-[#2f2f2e]")} aria-label="Previous slide" onClick={!disableSliding ? () => previousSlide() : _temp3}>{" "}<IconArrowLeftWhite /></button>;
      }} beforeSlide={index => {
        if (typeof props.beforeSlideFn === "function") {
          props.beforeSlideFn(index);
        }
      }} afterSlide={index_0 => {
        if (typeof props.afterSlideFn === "function") {
          props.afterSlideFn(index_0);
        }
      }} renderCenterRightControls={variant === "blog-slider" ? _temp4 : t16 => {
        const {
          nextSlide
        } = t16;
        if (hideNav || navType === "Dots" || finalSlides.length < visibleSlides) {
          return null;
        }
        return <button type="button" className={fabricGridControls ? "items-center [background:none] border-0 flex justify-center w-full hover:cursor-pointer focus:outline-0 focus-visible:outline-4 focus-visible:outline-offset-[-4px]" : classNames("group/SliderButton w-[50px] h-[50px] rounded-[100px] !flex justify-center items-center opacity-100 text-[0] leading-[0] cursor-pointer text-transparent border border-solid border-gray-light6 outline-0 [transition:all_.2s_ease-in] hover:border-brand !bg-white left-2 transform-[translateX(-50%)]", "[&_svg_path]:[transition:all_.2s_ease-in] [&_svg_path]:fill-gray", "group-hover/SliderButton:[&_svg_path]:fill-[#2f2f2e]")} aria-label="Next slide" onClick={!disableSliding ? () => nextSlide() : _temp5}><IconArrowRightWhite /></button>;
      }} renderBottomCenterControls={t17 => {
        const {
          currentSlide,
          slideCount,
          goToSlide
        } = t17;
        if (navType === "Button" || hideNav || showLeftAndRightPartially) {
          return null;
        }
        const slideCountArray = [...Array(slideCount).keys()];
        return <div className="absolute block bottom-[-45px] w-full p-0 m-0 list-none text-center">{slideCountArray.map((slide, index_1) => <button className={classNames("border-0 rounded-[50%] h-2 !w-2 bg-[#0eb9cb] !m-1 relative inline-block", "[&:disabled]:bg-gray-light5", "[&_span]:inline-block [&_span]:h-5 [&_span]:w-full [&_span]:max-w-[30px]")} type="button" key={`${slide.url}bcc`} disabled={currentSlide === index_1} onClick={() => goToSlide(index_1)}><span /></button>)}</div>;
      }}>{finalSlides}</ForwardRefCarousel>}{isNewConsumerApp && <ForwardRefCarousel className="space-x-0 md:space-x-5" ref={carouselRef} showArrows={variant !== "blog-slider"} arrows={<CustomCarouselArrows variant={variant} />} height={fullHeight ? "100%" : isDesktop ? "450px" : "400px"} heightMode="max" wrapAround={showLeftAndRightPartially ? true : finalVisibleSlides < finalSlides.length} dragging={!showLeftAndRightPartially} swiping={!disableSliding} prevSlidesToScroll={showLeftAndRightPartially ? 1 : undefined} width="100%" beforeSlide={index_2 => {
        if (typeof props.beforeSlideFn === "function") {
          props.beforeSlideFn(index_2);
        }
      }} afterSlide={index_3 => {
        if (typeof props.afterSlideFn === "function") {
          props.afterSlideFn(index_3);
        }
      }}>{finalSlides}</ForwardRefCarousel>}</>;
    $[33] = disableSliding;
    $[34] = fabricGridControls;
    $[35] = finalSlides;
    $[36] = finalVisibleSlides;
    $[37] = fullHeight;
    $[38] = hideNav;
    $[39] = isDesktop;
    $[40] = isMobile;
    $[41] = isMobileMaterialSwatches;
    $[42] = navType;
    $[43] = props;
    $[44] = showLeftAndRightPartially;
    $[45] = slideIndex;
    $[46] = variant;
    $[47] = visibleSlides;
    $[48] = t14;
  } else {
    t14 = $[48];
  }
  let t15;
  if ($[49] !== t12 || $[50] !== t13 || $[51] !== t14) {
    t15 = <div className={t12} ref={containerRef}>{t13}{t14}</div>;
    $[49] = t12;
    $[50] = t13;
    $[51] = t14;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  return t15;
};
SliderComponent.propTypes = {
  afterSlideFn: PropTypes.func,
  beforeSlideFn: PropTypes.func,
  children: PropTypes.arrayOf(PropTypes.node),
  deviceOverride: PropTypes.bool,
  disableSliding: PropTypes.bool,
  fabricGridControls: PropTypes.bool,
  finalVisibleSlides: PropTypes.number,
  isMobileMaterialSwatches: PropTypes.bool,
  navType: PropTypes.string,
  variant: PropTypes.oneOf(['blog-slider', 'collections-slider', 'material-swatches-sidebar']),
  fullHeight: PropTypes.bool
};
export default SliderComponent;
function _temp(i) {
  return Math.max(i - 1, 0);
}
function _temp2() {
  return null;
}
function _temp3() {}
function _temp4() {
  return null;
}
function _temp5() {}